import './ActivityLine.scss'
import { useIsFetching, type Query } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { clx } from '../../ui'

export interface ActivityLineProps {
  /** Show only after fetching has lasted this long, so quick fetches never flicker. */
  delay?: number
  className?: string
}

/**
 * A fetch the user is waiting for: a first load (nothing to show yet), or a
 * refetch of data that was invalidated (Refresh, a save). A silent refresh
 * of data already on screen (stale on mount) is not one: the content is
 * there, so the line would only look stuck.
 */
export function isAwaitedFetch(query: Query): boolean {
  return query.state.data === undefined || query.state.isInvalidated
}

/**
 * Thin indeterminate bar for fetches (ADR 0012, 0021): visible while an
 * awaited react-query fetch is in flight for longer than `delay` ms. Its motion
 * is essential, so it animates under reduced motion too (ADR 0021).
 */
export function ActivityLine({ delay = 300, className }: ActivityLineProps) {
  const isFetching = useIsFetching({ predicate: isAwaitedFetch }) > 0
  const [hasWaited, setHasWaited] = useState(false)

  useEffect(() => {
    if (!isFetching) return
    const timer = window.setTimeout(() => {
      setHasWaited(true)
    }, delay)
    return () => {
      window.clearTimeout(timer)
      setHasWaited(false)
    }
  }, [isFetching, delay])

  if (!isFetching || !hasWaited) return null
  return (
    <div
      role="progressbar"
      aria-label="Loading"
      aria-busy="true"
      className={clx('activity-line', className)}
    >
      <span className="activity-line__bar" data-motion="essential" />
    </div>
  )
}
